// BL-177 Step 2d (section 10): the signature plumbing shared by the reply composer and the New message panel.
//
// - useSignatureContext: company and personal settings, with load failures exposed so sends can be blocked.
// - signOutgoingBody: append the configured signature to each eligible staff message; templates never.

import { useEffect, useState } from 'react';
import {
    appendSignature,
    loadEmailCompanyBlock,
    loadSignatureContext,
    resolveActiveSignatureCompany,
} from '../../lib/signatures';

export function useSignatureContext(client, orgId, userId, companyContext = {}) {
    const [state, setState] = useState({ context: null, loading: true, error: '' });
    const doorEntityId = companyContext?.platformDoorContext?.entity_id || '';
    const headerEntityId = companyContext?.headerBrandEntityId || '';
    useEffect(() => {
        let active = true;
        setState({ context: null, loading: true, error: '' });
        if (typeof client?.rpc !== 'function') {
            setState({ context: null, loading: false, error: 'Signature settings are unavailable. Sending is disabled.' });
            return undefined;
        }
        (async () => {
            try {
                const context = await loadSignatureContext(client, { orgId: orgId || null, userId: userId || null });
                const company = { ...context.companySettings };
                if (company.always_on && company.email_block_enabled !== false && orgId && typeof client?.from === 'function') {
                    const activeCompany = await resolveActiveSignatureCompany(client, {
                        orgId,
                        userId,
                        doorEntityId,
                        headerEntityId,
                    });
                    company.email_block = await loadEmailCompanyBlock(
                        client,
                        orgId,
                        activeCompany.selectedId
                    );
                    if (!company.email_block) {
                        throw new Error('Choose which company to use for this email block in Settings › Communications › Signature.');
                    }
                }
                if (active) setState({ context: { ...context, companySettings: company }, loading: false, error: '' });
            } catch (failure) {
                if (active) setState({
                    context: null,
                    loading: false,
                    error: failure?.message || 'Could not load signature settings. Sending is disabled.',
                });
            }
        })();
        return () => { active = false; };
    }, [client, doorEntityId, headerEntityId, orgId, userId]);
    return { ...state.context, loading: state.loading, error: state.error };
}

export function useAlreadySignedToday() {
    return [false, () => {}];
}

/**
 * The body to send for ONE message. Every eligible staff message gets its configured signature.
 * @returns {Promise<{ body: string, claimed: boolean, claimAfterCreate: boolean }>}
 */
export async function signOutgoingBody({ body, signature, isTemplate = false }) {
    const unsigned = { body, claimed: false, claimAfterCreate: false };
    if (!signature?.applies || isTemplate) return unsigned;
    return { ...unsigned, body: appendSignature(body, signature) };
}

/**
 * BL-177 Step 2j (B2): what the "Signature ✓" chip on the composer toolbar shows, from the assembled signature.
 *   optional personal signature  -> a toggle: on = added (default), off = left out of THIS message only; the company part is always added
 *   required by the company      -> shown on, disabled (it can't be removed)
 *   required but not set up      -> shown off, disabled, with the reason
 *   no personal signature set up -> shown off, disabled; its menu offers "Set up your signature" (2026-10-25: the button no
 *                                  longer disappears, which read as "signatures are broken"). Display only: what is sent is
 *                                  still decided by signOutgoingBody / the assembled signature, unchanged.
 *   a template / automatic message, or settings still loading or failed to load (available: false) -> no chip
 * @returns {null | { state: 'on'|'off'|'required'|'missing'|'none', on: boolean, disabled: boolean, title: string, onToggle?: () => void, setupHref?: string }}
 */
export const MY_SIGNATURE_SETTINGS_PATH = '/settings/communications/signature';

export function signatureChipFor(signature, { removeMine = false, onToggle, available = true } = {}) {
    if (!signature) return null;
    if (signature.personalRequiredMissing) {
        return {
            state: 'missing', on: false, disabled: true,
            title: 'Your company asks for a personal signature and you have not set yours up yet. The company signature is added.',
            personalPart: '', companyPart: signature.companyPart || '',
        };
    }
    if (signature.canRemoveMine) {
        return {
            state: removeMine ? 'off' : 'on',
            on: !removeMine,
            disabled: false,
            personalPart: signature.personalAvailablePart || signature.personalPart || '',
            companyPart: signature.companyPart || '',
            title: removeMine
                ? 'Your own signature is left off this message only. The company signature is still added.'
                : 'Your signature is added. Turn it off for this message only; the company signature is always added.',
            onToggle,
        };
    }
    if (signature.applies && signature.personalPart) {
        return {
            state: 'required', on: true, disabled: true,
            title: 'Your company requires your signature on every message.',
            personalPart: signature.personalAvailablePart || signature.personalPart || '', companyPart: signature.companyPart || '',
        };
    }
    // Not for templates / automatic messages (never signed), nor while the settings are loading or failed (that error shows).
    if (!signature.personalPart && available && !['template', 'automatic', 'unsupported_channel'].includes(signature.reason)) {
        const personalOff = signature.mode === 'off';
        return {
            state: 'none', on: false, disabled: true,
            title: personalOff
                ? 'Your company has personal signatures turned off.'
                : 'You have not set up a personal signature yet.',
            personalPart: '', companyPart: signature.companyPart || '',
            setupHref: personalOff ? '' : MY_SIGNATURE_SETTINGS_PATH,
        };
    }
    return null;
}
